/* IC Hero Editorial: swaps the background photo to match the hovered/focused card,
   rotates photos on touch screens, and sizes the hero to the space below the header. */
(() => {
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const noHover = matchMedia('(hover: none)');

  function init(root) {
    if (!root || root.dataset.hxReady) return;
    root.dataset.hxReady = '1';
    const layers = [...root.querySelectorAll('.ic-hx__layer')];
    const cards = [...root.querySelectorAll('.ic-hx__card')];
    const nav = root.querySelector('.ic-hx__cards');
    let timer = null;
    let idx = 0;

    const setTop = () => {
      const top = root.getBoundingClientRect().top + window.scrollY;
      root.style.setProperty('--hx-top', Math.max(0, Math.min(top, 280)) + 'px');
    };
    setTop();
    addEventListener('resize', setTop, { passive: true });

    // Scroll effect: 0 while the hero is fully in view, 1 once it has mostly scrolled away
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const r = root.getBoundingClientRect();
        const start = Math.max(0, r.top + window.scrollY - innerHeight);
        const d = Math.max(0, window.scrollY - start);
        const p = Math.min(1, d / (r.height * 0.8));
        root.style.setProperty('--hx-p', p.toFixed(4));
        // Parallax: the photo moves at (1 - strength) of the scroll speed, but never drops below the top
        // of the screen while the hero is under it (that uncovered the dark hero background as a bar).
        // Only with Lenis (desktop): it moves the page and the photo in the same frame. With native touch
        // scrolling the photo lags a frame behind the page, which jitters and opens a dark gap at the top
        // when scrolling back up, so on phones and tablets the photo just scrolls with the hero.
        const k = window.icLenis ? parseFloat(root.dataset.parallax) || 0 : 0;
        if (!k) root.style.removeProperty('--hx-par');
        else {
          const heroDocTop = r.top + window.scrollY;
          const par = Math.min(Math.min(d, r.height) * k, Math.max(0, window.scrollY - heroDocTop));
          root.style.setProperty('--hx-par', par + 'px');
        }
      });
    };
    if (!reduced.matches) { addEventListener('scroll', onScroll, { passive: true }); onScroll(); }

    // Knocked-out header: measure header + sub-nav, go solid once the hero is behind the header
    if (root.classList.contains('ic-hx--knockout')) {
      const headerSec = document.querySelector('.shopify-section-header');
      const subnav = document.querySelector('.ic-subnav');
      const measure = () => {
        if (!headerSec) return;
        let h = 0, on = false;
        document.querySelectorAll('.shopify-section-group-header-group').forEach(s => {
          if (s === headerSec) on = true;
          if (on) h += s.getBoundingClientRect().height;
        });
        document.body.style.setProperty('--hx-overlap', Math.round(h) + 'px');
      };
      let rafK = 0;
      const solid = () => {
        if (rafK) return;
        rafK = requestAnimationFrame(() => {
          rafK = 0;
          const edge = headerSec ? headerSec.getBoundingClientRect().bottom : 0;
          document.body.classList.toggle('ic-hx-solid', root.getBoundingClientRect().bottom <= edge + 1);
          // Sub-nav fades and blurs out as it slides up under the transparent header
          if (subnav) {
            const s = subnav.getBoundingClientRect();
            const q = Math.min(1, Math.max(0, (edge - s.top) / (s.height * 0.7)));
            subnav.style.opacity = (1 - q).toFixed(3);
            if (!noHover.matches) subnav.style.filter = q > 0 ? 'blur(' + (q * 8).toFixed(2) + 'px)' : '';  // blur per frame is too heavy on phones
            subnav.style.pointerEvents = q > 0.5 ? 'none' : '';
          }
        });
      };
      measure(); solid();
      addEventListener('resize', () => { measure(); setTop(); solid(); }, { passive: true });
      addEventListener('scroll', solid, { passive: true });
      setTop();
    }

    const show = (target, card) => {
      layers.forEach(l => l.classList.toggle('is-active', l.dataset.layer === target));
      cards.forEach(c => c.classList.toggle('is-active', c === card));
    };
    const reset = () => show('default', null);

    cards.forEach(card => {
      const go = () => { stop(); show(card.dataset.target, card); };
      card.addEventListener('pointerenter', e => { if (e.pointerType !== 'touch') go(); });
      card.addEventListener('focus', go);
    });
    if (nav) {
      nav.addEventListener('pointerleave', e => { if (e.pointerType !== 'touch') reset(); });
      nav.addEventListener('focusout', e => { if (!nav.contains(e.relatedTarget)) reset(); });
    }

    // Touch screens: cycle default photo -> each card's photo
    const cycle = cards.filter(c => c.dataset.target !== 'default');
    const stop = () => { clearInterval(timer); timer = null; };
    const start = () => {
      if (timer || !cycle.length || root.dataset.autoplay !== 'true' || !noHover.matches || reduced.matches) return;
      const ms = (parseFloat(root.dataset.interval) || 4) * 1000;
      timer = setInterval(() => {
        idx = (idx + 1) % (cycle.length + 1);
        idx === 0 ? reset() : show(cycle[idx - 1].dataset.target, cycle[idx - 1]);
      }, ms);
    };
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(([e]) => (e.isIntersecting ? start() : stop()), { threshold: 0.25 }).observe(root);
    } else start();

    root._hx = { show, reset, stop };
  }

  const all = () => document.querySelectorAll('.ic-hx').forEach(init);
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', all) : all();

  // Theme editor: re-init on reload, preview the selected card's photo
  document.addEventListener('shopify:section:load', e => init(e.target.querySelector('.ic-hx')));
  document.addEventListener('shopify:block:select', e => {
    const card = e.target.closest('.ic-hx__card');
    const root = e.target.closest('.ic-hx');
    if (card && root && root._hx) { root._hx.stop(); root._hx.show(card.dataset.target, card); }
  });
  document.addEventListener('shopify:block:deselect', e => {
    const root = e.target.closest('.ic-hx');
    if (root && root._hx) root._hx.reset();
  });
})();
